import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { changeLanguageWithReload } from "../i18n";
import settingsIcon from "../assets/icons/settings.png";
import dashboardIcon from "../assets/images/dashboard.png";
import accountsIcon from "../assets/images/accounts.png";
import fundsIcon from "../assets/images/funds.png";
import transactionsIcon from "../assets/images/transactions.png";
import ibDashboardIcon from "../assets/images/ib-dashboard.png";
import userProfileIcon from "../assets/images/user-profile.png";
import rsaController from "../utils/RSA-controller";
import {
  clearHomePageInfosClientCache,
  deleteFcmTokenOnLogout,
  getHomePageInfos,
} from "../utils/apiClient";
import {
  getPermissionControllers,
  hasCustomerPermission,
  hasCustomerPermissionOnEachSide,
  hasBrokerPermission,
  isAccountPermissionDisabled,
  canDeposit,
  canWithdraw,
  isBroker,
  isAccountApprovalLocked,
  getKycStatusValue,
} from "../utils/permissionHelper";
import PermissionCustomerModelController from "../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../controller/model/PermissionBrokerModelController";
import { useTheme } from "../contexts/ThemeContext";
import ProfileInfoController from "../controller/profileInfoController";
import { fetchDocumentImage } from "../utils/apiClient";
import SupportChatWidget from "./SupportChatWidget";
import AppTopHeader from "./AppTopHeader";
import Popup from "./Popup";
import { deleteWebFcmToken } from "../services/firebaseMessagingService";
import { resetUiPreferencesToDefaults } from "../utils/userPreferences";
import { isTabletOrBelow, TABLET_MAX_WIDTH } from "../utils/layoutBreakpoints";
import { TOUR_SIDEBAR_DROPDOWN } from "../data/pageGuides";

export default function Sidebar({ user = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [activeItem, setActiveItem] = useState("dashboard");
  // Initialize collapsed state based on screen size
  const [isCollapsed, setIsCollapsed] = useState(() => isTabletOrBelow());
  const [isMobile, setIsMobile] = useState(() => isTabletOrBelow());
  const [profileUrl, setProfileUrl] = useState(null);
  const [sidebarAvatarLoadFailed, setSidebarAvatarLoadFailed] = useState(false);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [hydratedUser, setHydratedUser] = useState(null);
  const { isDarkMode, toggleTheme } = useTheme();
  
  const isInitialMountRef = useRef(true);
  
  // Helper function to close sidebar on mobile
  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= TABLET_MAX_WIDTH) {
      setIsCollapsed(true);
    }
  };

  // Tablet + mobile: overlay sidebar, collapsed by default
  useEffect(() => {
    const checkCompactLayout = () => {
      const compact = window.innerWidth <= TABLET_MAX_WIDTH;
      setIsMobile(compact);

      if (compact) {
        setIsCollapsed(true);
      }
    };

    checkCompactLayout();

    window.addEventListener("resize", checkCompactLayout);

    return () => window.removeEventListener("resize", checkCompactLayout);
  }, []);
  
  // Add/remove class to body when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [isCollapsed]);

  useEffect(() => {
    document.body.classList.add("has-app-top-header");
    return () => document.body.classList.remove("has-app-top-header");
  }, []);

  useEffect(() => {
    let cancelled = false;

    const readStoredUser = () => {
      try {
        const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData");
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (_) {
        return null;
      }
    };

    const hydrateSidebarUser = async () => {
      const storedUser = readStoredUser();
      if (storedUser) {
        if (!cancelled) setHydratedUser(storedUser);
      } else if (user) {
        if (!cancelled) setHydratedUser(user);
      }

      // Always refresh from API so permission changes from admin appear without
      // requiring a full browser wipe of sessionStorage.
      try {
        const response = await getHomePageInfos();
        const data = response?.decryptedData || response?.data;
        const loadedUser = data?.DATA || null;
        if (!loadedUser || cancelled) return;

        setHydratedUser(loadedUser);
        try {
          sessionStorage.setItem("userData", JSON.stringify(loadedUser));
          sessionStorage.setItem("userDataTimestamp", Date.now().toString());
        } catch (_) {}
      } catch (_) {
        if (!cancelled && !storedUser && !user) setHydratedUser(null);
      }
    };

    hydrateSidebarUser();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onProfileUpdated = (event) => {
      const nextUser = event?.detail?.user;
      if (nextUser) {
        setHydratedUser(nextUser);
        return;
      }

      try {
        const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData");
        if (raw) setHydratedUser(JSON.parse(raw));
      } catch (_) {}
    };

    window.addEventListener("mfg:user-profile-updated", onProfileUpdated);
    return () => window.removeEventListener("mfg:user-profile-updated", onProfileUpdated);
  }, []);

  const sidebarUser = hydratedUser;

  const handleToggleSidebar = () => {
    if (isMobile && !isCollapsed) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };
  
  // Resolve profile image URL (never the KYC selfie)
  useEffect(() => {
    let cancelled = false;

    const resolveAndFetch = async (rawData) => {
      const docUrls = ProfileInfoController.extractDocumentUrls(rawData);
      const filePath = docUrls.profile;
      if (!filePath) return null;
      return await fetchDocumentImage(filePath);
    };

    const getCachedUserData = () => {
      try {
        const raw = sessionStorage.getItem("userData");
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (_) {
        return null;
      }
    };

    (async () => {
      // Try extracting from current sidebar user first.
      if (sidebarUser) {
        const blobUrl = await resolveAndFetch(sidebarUser);
        if (!cancelled && blobUrl) { setProfileUrl(blobUrl); return; }
      }

      // Then try cached session user to avoid extra API calls on route switches.
      const cachedUser = getCachedUserData();
      if (cachedUser) {
        const blobUrl = await resolveAndFetch(cachedUser);
        if (!cancelled && blobUrl) { setProfileUrl(blobUrl); return; }
      }

      // Fall back to fetching full profile with documents
      const res = await ProfileInfoController.getProfileInfo();
      if (cancelled) return;
      if (res.success && res.data?.raw) {
        const blobUrl = await resolveAndFetch(res.data.raw);
        if (!cancelled && blobUrl) setProfileUrl(blobUrl);
      }
    })();

    return () => { cancelled = true; };
  }, [sidebarUser]);

  const sidebarUserDisplayInitial = useMemo(() => {
    const displayName = ProfileInfoController.getDisplayName(sidebarUser || {});
    const raw =
      displayName !== "User"
        ? displayName
        : sidebarUser?.user_information?.EMAIL ||
          sidebarUser?.userInformation?.EMAIL ||
          (typeof window !== "undefined"
            ? sessionStorage.getItem("userEmail") || localStorage.getItem("userEmail")
            : "") ||
          "";
    const s = String(raw).trim();
    if (!s) return "?";
    const first = [...s][0];
    if (!first) return "?";
    try {
      return first.toLocaleUpperCase(i18n.language || undefined);
    } catch (_) {
      return first.toUpperCase();
    }
  }, [sidebarUser, i18n.language]);

  const sidebarCustomerId = useMemo(() => {
    const info = sidebarUser?.user_information || sidebarUser?.userInformation || {};
    const raw =
      sidebarUser?.CLIENT_ID ??
      sidebarUser?.clientId ??
      sidebarUser?.client_id ??
      sidebarUser?.CUSTOMER_ID ??
      sidebarUser?.customer_id ??
      info.CLIENT_ID ??
      info.clientId ??
      info.CUSTOMER_ID ??
      info.customer_id ??
      "";
    return String(raw).trim();
  }, [sidebarUser]);

  useEffect(() => {
    setSidebarAvatarLoadFailed(false);
  }, [profileUrl]);

  // Set active item based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/pages/dashboard" || path === "/dashboard") {
      setActiveItem("dashboard");
    } else if (path === "/pages/marketing-tools") {
      setActiveItem("marketingTools-Marketing Plan");
      setOpenDropdowns((prev) => ({ ...prev, marketingTools: true }));
    } else if (path === "/pages/profile-information") {
      setActiveItem("profile-Profile");
      setOpenDropdowns((prev) => ({ ...prev, profile: true }));
    } else if (path === "/pages/trading-information") {
      setActiveItem("profile-Trading Info");
      setOpenDropdowns((prev) => ({ ...prev, profile: true }));
    } else if (path === "/pages/financial-information") {
      setActiveItem("profile-Financial Info");
      setOpenDropdowns((prev) => ({ ...prev, profile: true }));
    } else if (path === "/pages/user-regulation") {
      setActiveItem("profile-Regulation");
      setOpenDropdowns((prev) => ({ ...prev, profile: true }));
    } else if (path === "/pages/cash-in") {
      setActiveItem("funds-Cash-In");
      // Open funds dropdown when on cash-in page
      setOpenDropdowns((prev) => ({ ...prev, funds: true }));
    } else if (path === "/pages/cash-out") {
      setActiveItem("funds-Cash-Out");
      // Open funds dropdown when on cash-out page
      setOpenDropdowns((prev) => ({ ...prev, funds: true }));
    } else if (path === "/pages/exchange") {
      setActiveItem("funds-Exchange");
      // Open funds dropdown when on exchange page
      setOpenDropdowns((prev) => ({ ...prev, funds: true }));
    } else if (path === "/pages/pay-with-card") {
      setActiveItem("funds-Pay With Card");
      setOpenDropdowns((prev) => ({ ...prev, funds: true }));
    } else if (path === "/pages/accounts/list") {
      setActiveItem("accounts-List");
      // Open accounts dropdown when on accounts list page
      setOpenDropdowns((prev) => ({ ...prev, accounts: true }));
    } else if (path === "/pages/accounts/demo") {
      setActiveItem("accounts-Demo Accounts");
      setOpenDropdowns((prev) => ({ ...prev, accounts: true }));
    } else if (path === "/pages/transfer") {
      setActiveItem("funds-Internal Transfer");
      setOpenDropdowns((prev) => ({ ...prev, funds: true }));
    } else if (path === "/pages/transactions/history") {
      setActiveItem("transactions-History");
      // Open transactions dropdown when on history page
      setOpenDropdowns((prev) => ({ ...prev, transactions: true }));
    } else if (path === "/pages/transactions/cash-in-requests") {
      setActiveItem("transactions-Cash-In Requests");
      // Open transactions dropdown when on cash-in requests page
      setOpenDropdowns((prev) => ({ ...prev, transactions: true }));
    } else if (path === "/pages/transactions/cash-out-requests") {
      setActiveItem("transactions-Cash-Out Requests");
      // Open transactions dropdown when on cash-out requests page
      setOpenDropdowns((prev) => ({ ...prev, transactions: true }));
    } else if (path === "/pages/transactions/pending") {
      setActiveItem("transactions-Pending");
      // Open transactions dropdown when on pending transactions page
      setOpenDropdowns((prev) => ({ ...prev, transactions: true }));
    } else if (path === "/pages/ibDashboard/dashboard") {
      setActiveItem("ibDashboard-Overview");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/broker-tools" || path.startsWith("/pages/ibDashboard/broker-tools/")) {
      setActiveItem("marketingTools-Broker Tools");
      setOpenDropdowns((prev) => ({ ...prev, marketingTools: true, ibDashboard: false }));
    } else if (path === "/pages/ibDashboard/clients-list") {
      setActiveItem("ibDashboard-Clients List");
      // Open IB Dashboard dropdown when on clients list page
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/clients-tree") {
      setActiveItem("ibDashboard-Clients Tree");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/transfer-to-client") {
      setActiveItem("ibDashboard-Transfer To Client");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/transfer-from-client") {
      setActiveItem("ibDashboard-Transfer From Client");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/collect-transaction") {
      setActiveItem("ibDashboard-Collect Transaction");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/ibDashboard/send-notification") {
      setActiveItem("ibDashboard-Send Notification");
      setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
    } else if (path === "/pages/settings/change-pin") {
      setActiveItem("settings-Change Password");
      setOpenDropdowns((prev) => ({ ...prev, settings: true }));
    } else if (path === "/pages/settings/active-devices") {
      setActiveItem("settings-Active Devices");
      setOpenDropdowns((prev) => ({ ...prev, settings: true }));
    } else if (path === "/pages/settings/translation") {
      setActiveItem("settings-Translated");
      setOpenDropdowns((prev) => ({ ...prev, settings: true }));
    } else if (path === "/pages/legal/privacy-policy") {
      setActiveItem("legal-Privacy Policy");
      setOpenDropdowns((prev) => ({ ...prev, legal: true }));
    } else if (path === "/pages/legal/terms-of-services") {
      setActiveItem("legal-Terms of Services");
      setOpenDropdowns((prev) => ({ ...prev, legal: true }));
    } else if (path === "/pages/legal/risk-disclosure") {
      setActiveItem("legal-Risk Disclosure");
      setOpenDropdowns((prev) => ({ ...prev, legal: true }));
    } else if (path === "/pages/support/faq") {
      setActiveItem("support-FAQ");
      setOpenDropdowns((prev) => ({ ...prev, support: true }));
    } else if (path === "/pages/support/about-us") {
      setActiveItem("support-About Us");
      setOpenDropdowns((prev) => ({ ...prev, support: true }));
    }
  }, [location.pathname]);

  const [openDropdowns, setOpenDropdowns] = useState({
    accounts: false,
    funds: false,
    transactions: false,
    ibDashboard: false,
    marketingTools: false,
    profile: false,
    legal: false,
    support: false,
    settings: false,
  });

  useEffect(() => {
    const onReveal = (event) => {
      const key = event?.detail?.key;
      if (!key) return;
      setIsCollapsed(false);
      let dropdown = TOUR_SIDEBAR_DROPDOWN[key];
      if (key === "collect_transaction" && location.pathname.includes("/ibDashboard")) {
        dropdown = "ibDashboard";
      }
      if (!dropdown) return;
      setOpenDropdowns((prev) => (prev[dropdown] ? prev : { ...prev, [dropdown]: true }));
    };
    window.addEventListener("page-tour:reveal", onReveal);
    return () => window.removeEventListener("page-tour:reveal", onReveal);
  }, [location.pathname]);

  const toggleDropdown = (item) => {
    // Don't set the dropdown title as active, just toggle the dropdown
    setOpenDropdowns((prev) => {
      // Close all other dropdowns and toggle the clicked one
      const newState = {
        accounts: false,
        funds: false,
        transactions: false,
        ibDashboard: false,
        marketingTools: false,
        profile: false,
        legal: false,
        support: false,
        settings: false,
      };
      newState[item] = !prev[item];
      return newState;
    });
  };

  const handleDropdownItemClick = (parentItem, subItem) => {
    setActiveItem(`${parentItem}-${subItem}`); // Set the sub-item as active
    // You can add navigation logic here if needed
  };

  // Get permission controllers from user data (similar to Flutter InformationController)
  const permissions = useMemo(() => getPermissionControllers(sidebarUser), [sidebarUser]);
  const {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  } = permissions;

  /**
   * Permission checks for sidebar items (based on Flutter home_screen.dart and main_screen.dart)
   * 
   * Items that ALWAYS show (no permissions required):
   * - Dashboard
   * - Profile (and its sub-items)
   * - Settings (and its sub-items)
   * - Security
   * 
   * Items that require permissions:
   * - Accounts dropdown: PER__MANAGEMENT_REAL_ACCOUNT
   * - Funds items: Various customer/broker permissions
   * - Transactions items: Various customer/broker permissions
   * - IB Dashboard: Broker permissions only
   */
  
  // Accounts / Funds / Transactions visibility:
  // Check global approval lock (profile completion + KYC approval status)
  // When true, financial-related sidebar items should be hidden entirely.
  const approvalLocked = useMemo(() => isAccountApprovalLocked(sidebarUser), [sidebarUser]);
  
  // Check if user has any account-related permission
  const hasManageRealAccount = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__MANAGEMENT_REAL_ACCOUNT
  );
  const hasAddRealAccount = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT
  );
  
  // Add: requires PER__ADD_REAL_ACCOUNT and not approval locked
  const canAddRealAccount = !approvalLocked && hasAddRealAccount;

  // Show Accounts dropdown if user has any account permission
  // AND the account is not globally locked (profile incomplete or approval pending)
  // This prevents users in "Complete Your Profile" or "Account Approval Required"
  // states from even seeing account-related navigation items.
  const showAccountsDropdown = !approvalLocked && (hasManageRealAccount || hasAddRealAccount);

  // Funds dropdown permissions
  const canCashIn = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_IN
  );
  // Raw KYC status used to hide Cash-Out when in "Account Verification Required" stage
  const kycStatusValue = useMemo(() => getKycStatusValue(sidebarUser), [sidebarUser]);

  const canCashOutBase = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_OUT
  );
  const canInternalTransfer = hasCustomerPermissionOnEachSide(
    customerPerm,
    customerDevicePerm,
    [
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_MT5,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5,
    ]
  );
  const canExchange = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_EXCHANGE
  );
  const canDepositByCard = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_DEPOSIT_BY_CARD
  );
  
  // Hide Cash-Out when KYC is in "Account Verification Required" stage:
  // kycStatus 1 (APPROVED), -3 (UPDATED_KYC), -2 (HAS_KYC_ERROR)
  const isAccountVerificationRequired =
    kycStatusValue === 1 || kycStatusValue === -3 || kycStatusValue === -2;

  // Final flag for showing Cash-Out in sidebar Funds dropdown
  const showCashOutInSidebar = canCashOutBase && !isAccountVerificationRequired;

  // Broker deposit/withdraw permissions
  const canDepositClient = canDeposit(brokerPerm, brokerDevicePerm);
  const canWithdrawClient = canWithdraw(brokerPerm, brokerDevicePerm);

  // Transactions dropdown permissions
  const canViewPendingRequest = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_PENDING_REQUEST
  );
  const canViewHistory = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_HISTORY
  );
  const canViewCashInRequests = canCashIn && canViewPendingRequest;
  const canViewCashOutRequests = canCashOutBase && canViewPendingRequest;

  // IB Dashboard permissions (broker only)
  const userIsBroker = isBroker(sidebarUser);
  const canViewClient = hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_VIEW_CLIENT
  );
  const canViewClientTreeList = !isAccountPermissionDisabled(
    brokerPerm,
    PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TREE_LIST
  );
  const canSendNotification = hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_SEND_NOTIFICATION
  );
  const canCollectTransaction = userIsBroker && (
    hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_COLLECT_CASHIN
    ) ||
    hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_COLLECT_CASH_OUT
    ) ||
    hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_TRANSFER_CASHIN
    ) ||
    hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_TRANSFER_CASH_OUT
    )
  );
  const canViewBrokerTools = userIsBroker && hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
  );
  const canViewClientsList = userIsBroker && canViewClient;
  const canViewClientsTree = userIsBroker && canViewClientTreeList;

  // Check if any IB Dashboard item should be shown
  // Also hidden when profile is incomplete / approval pending (same as Funds & Transactions)
  const showIBDashboard = !approvalLocked && userIsBroker && (
    canSendNotification ||
    canViewClientsList ||
    canViewClientsTree ||
    canDepositClient ||
    canWithdrawClient ||
    canCollectTransaction
  );

  // Route flags to ensure dropdowns show when navigating directly by URL
  const onTransactionsHistoryRoute = location.pathname === "/pages/transactions/history";
  const onCashInRequestsRoute = location.pathname === "/pages/transactions/cash-in-requests";
  const onCashOutRequestsRoute = location.pathname === "/pages/transactions/cash-out-requests";
  const onPendingTransactionsRoute = location.pathname === "/pages/transactions/pending";
  const hideChatOnTablePages =
    location.pathname === "/pages/ibDashboard/dashboard" ||
    location.pathname === "/pages/ibDashboard/broker-tools" ||
    location.pathname.startsWith("/pages/ibDashboard/broker-tools/") ||
    location.pathname === "/pages/ibDashboard/clients-list" ||
    location.pathname === "/pages/ibDashboard/clients-tree" ||
    location.pathname.startsWith("/pages/ibDashboard/client-accounts/");

  /** No floating chat bubble; chat opens only from "Contact Support" (or notifications) on these routes. */
  const hideSupportChatLauncher =
    location.pathname.startsWith("/pages/accounts/") ||
    location.pathname.startsWith("/pages/transactions/") ||
    location.pathname === "/pages/cash-in" ||
    location.pathname === "/pages/cash-out" ||
    location.pathname === "/pages/exchange" ||
    location.pathname === "/pages/pay-with-card" ||
    location.pathname === "/pages/transfer" ||
    location.pathname === "/pages/ibDashboard/transfer-to-client" ||
    location.pathname === "/pages/ibDashboard/transfer-from-client" ||
    location.pathname === "/pages/ibDashboard/collect-transaction" ||
    location.pathname === "/pages/ibDashboard/broker-tools" ||
    location.pathname.startsWith("/pages/ibDashboard/broker-tools/");

  // Check if a dropdown item is active (activeItem contains a dash)
  const hasActiveDropdownItem = activeItem.includes('-') && activeItem !== 'dashboard';

  /**
   * Routes the user can open from the header search. Must stay aligned with sidebar JSX:
   * only items that are rendered and have a real navigate() target (same permission gates).
   */
  const headerSearchNavItems = useMemo(() => {
    const ns = "mobile";
    const items = [];
    const push = (label, path) => {
      if (!path || label == null || String(label).trim() === "") return;
      items.push({ label: String(label).trim(), path });
    };

    push(t("dashboard", { ns }), "/pages/dashboard");

    if (showAccountsDropdown) {
      push(t("accounts", { ns }), "/pages/accounts/list");
      push(t("real_accounts", { ns }), "/pages/accounts/list");
      push(t("demo_accounts", { ns }), "/pages/accounts/demo");
    }

    const showFundsDropdown =
      !approvalLocked &&
      (canCashIn ||
        showCashOutInSidebar ||
        canInternalTransfer ||
        canExchange ||
        canDepositByCard ||
        canDepositClient ||
        canWithdrawClient);

    if (showFundsDropdown) {
      let fundsDefaultPath = null;
      if (canCashIn) fundsDefaultPath = "/pages/cash-in";
      else if (showCashOutInSidebar) fundsDefaultPath = "/pages/cash-out";
      else if (canExchange) fundsDefaultPath = "/pages/exchange";
      else if (canDepositByCard) fundsDefaultPath = "/pages/pay-with-card";
      else if (canInternalTransfer) fundsDefaultPath = "/pages/transfer";

      if (canCashIn) push(t("cashin", { ns }), "/pages/cash-in");
      if (showCashOutInSidebar) push(t("cashout", { ns }), "/pages/cash-out");
      if (canInternalTransfer) push(t("internal_transfer", { ns }), "/pages/transfer");
      if (canExchange) push(t("exchange", { ns }), "/pages/exchange");
      if (canDepositByCard) push(t("pay_with_card", { ns }), "/pages/pay-with-card");
      /* Funds rows for transfer to/from client when !showIBDashboard have no route in sidebar — omit from search. */

      if (fundsDefaultPath) push(t("funds", { ns }), fundsDefaultPath);
    }

    const showTransactionsDropdown =
      !approvalLocked &&
      (canViewPendingRequest ||
        canViewCashInRequests ||
        canViewCashOutRequests ||
        canViewHistory ||
        canCollectTransaction ||
        onTransactionsHistoryRoute ||
        onCashInRequestsRoute ||
        onCashOutRequestsRoute ||
        onPendingTransactionsRoute);

    if (showTransactionsDropdown) {
      let txDefaultPath = null;
      if (canViewCashInRequests) txDefaultPath = "/pages/transactions/cash-in-requests";
      else if (canViewCashOutRequests) txDefaultPath = "/pages/transactions/cash-out-requests";
      else if (canViewPendingRequest || onPendingTransactionsRoute) {
        txDefaultPath = "/pages/transactions/pending";
      } else if (canViewHistory || onTransactionsHistoryRoute) {
        txDefaultPath = "/pages/transactions/history";
      }

      if (canViewCashInRequests) {
        push(t("cash_in_requests", { ns }), "/pages/transactions/cash-in-requests");
      }
      if (canViewCashOutRequests) {
        push(t("cash_out_requests", { ns }), "/pages/transactions/cash-out-requests");
      }
      if (canViewPendingRequest || onPendingTransactionsRoute) {
        push(t("pending_transaction", { ns }), "/pages/transactions/pending");
      }
      if (canViewHistory || onTransactionsHistoryRoute) {
        push(t("history", { ns }), "/pages/transactions/history");
      }

      if (txDefaultPath) push(t("transactions", { ns }), txDefaultPath);
    }

    if (showIBDashboard) {
      push(t("ib_dashboard", { ns }), "/pages/ibDashboard/dashboard");
      if (canViewClientsList) {
        push(t("clients_list", { ns }), "/pages/ibDashboard/clients-list");
      }
      if (canViewClientsTree) {
        push(t("ib_clients_tree_tab", { ns }) || "Clients Tree", "/pages/ibDashboard/clients-tree");
      }
      if (canDepositClient) push(t("transfer_to_client", { ns }), "/pages/ibDashboard/transfer-to-client");
      if (canWithdrawClient) push(t("transfer_from_client", { ns }), "/pages/ibDashboard/transfer-from-client");
      if (canCollectTransaction) push(t("collect_transaction", { ns }), "/pages/ibDashboard/collect-transaction");
      if (canSendNotification) push(t("send_notification", { ns }), "/pages/ibDashboard/send-notification");
    } else if (canCollectTransaction) {
      push(t("collect_transaction", { ns }), "/pages/ibDashboard/collect-transaction");
    }

    push(t("marketing_tools", { ns, defaultValue: "Marketing Tools" }), "/pages/marketing-tools");
    push(t("marketing_plan", { ns, defaultValue: "Marketing Plan" }), "/pages/marketing-tools");
    if (canViewBrokerTools) {
      push(t("ib_broker_tools", { ns, defaultValue: "Broker tools" }), "/pages/ibDashboard/broker-tools");
    }
    push(t("profile", { ns }), "/pages/profile-information");
    push(t("profile_information", { ns }), "/pages/profile-information");
    push(t("user_regulation", { ns }), "/pages/user-regulation");
    push(t("trading_information", { ns }), "/pages/trading-information");
    push(t("financial_information", { ns }), "/pages/financial-information");

    push(t("settings", { ns }), "/pages/settings/change-pin");
    push(t("change_password", { ns }), "/pages/settings/change-pin");
    push(t("active_devices", { ns }), "/pages/settings/active-devices");

    push(t("legal", { ns }), "/pages/legal/privacy-policy");
    push(t("privacy_policy", { ns }), "/pages/legal/privacy-policy");
    push(t("terms_of_services", { ns }), "/pages/legal/terms-of-services");
    push(t("risk_disclosure", { ns }), "/pages/legal/risk-disclosure");

    push(t("support", { ns }), "/pages/support/faq");
    push(t("faq", { ns }), "/pages/support/faq");
    push(t("about_us", { ns }), "/pages/support/about-us");

    const seen = new Set();
    return items.filter((entry) => {
      const key = `${entry.path}::${entry.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [
    t,
    i18n.language,
    sidebarUser,
    approvalLocked,
    showAccountsDropdown,
    canInternalTransfer,
    showCashOutInSidebar,
    canCashIn,
    canExchange,
    canDepositByCard,
    canDepositClient,
    canWithdrawClient,
    canViewPendingRequest,
    canViewCashInRequests,
    canViewCashOutRequests,
    canViewHistory,
    canCollectTransaction,
    onTransactionsHistoryRoute,
    onCashInRequestsRoute,
    onCashOutRequestsRoute,
    onPendingTransactionsRoute,
    showIBDashboard,
    canViewBrokerTools,
    canViewClientsList,
    canViewClientsTree,
  ]);

  const performLogout = async () => {
    // Mirror mobile logout: attempt backend FCM token delete first.
    const username =
      localStorage.getItem("userEmail") ||
      sessionStorage.getItem("userEmail") ||
      sidebarUser?.EMAIL ||
      sidebarUser?.email ||
      sidebarUser?.user_information?.EMAIL ||
      sidebarUser?.userInformation?.EMAIL ||
      null;
    await deleteFcmTokenOnLogout(username);
    await deleteWebFcmToken();

    // Clear token from storage
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    try {
      localStorage.removeItem("mfg_last_activity_at");
    } catch (_) {}

    // Drop home-page + user mirrors so the next account never inherits wallets/currencies from cache merge.
    clearHomePageInfosClientCache();

    // Clear user ID
    localStorage.removeItem("userId");
    sessionStorage.removeItem("userId");

    // NOTE:
    // Do NOT clear stored device identity on a normal logout.
    // The device-id must remain stable across sessions so the
    // backend and frontend both continue to recognize this
    // browser as the same trusted device.
    // clearStoredDeviceHeaders();

    // Clear RSA keys
    rsaController.clearUserKeys();

    // Clear login/session markers and other stored data
    localStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userEmail");
    sessionStorage.removeItem("userEmail");
    localStorage.removeItem("registration_step1_data");
    sessionStorage.removeItem("registration_step1_data");
    resetUiPreferencesToDefaults();

    try {
      window.dispatchEvent(new CustomEvent("mfg:notification-badge", { detail: { count: 0 } }));
    } catch (_) {}

    // Navigate to login page
    navigate("/home");

    // Reload page to clear all state
    window.location.reload();
  };

  const handleLogout = () => {
    closeSidebarOnMobile();
    setShowTranslationDialog(false);
    setShowLogoutDialog(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutDialog(false);
    await performLogout();
  };

  const collapseSidebar = () => {
    setIsCollapsed(true);
  };

  return (
    <>
      <AppTopHeader
        user={sidebarUser}
        profileUrl={profileUrl}
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        onToggleSidebar={handleToggleSidebar}
        onCollapseSidebar={collapseSidebar}
        sidebarSearchItems={headerSearchNavItems}
        onLogout={handleLogout}
      />
      {/* Mobile backdrop overlay */}
      {!isCollapsed && isMobile && (
        <div 
          className="sidebar-mobile-backdrop"
          onClick={() => setIsCollapsed(true)}
          aria-label="Close sidebar"
        />
      )}
      <aside className={`sidebar sidebar--finvault ${hasActiveDropdownItem ? 'has-active-dropdown-item' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'sidebar-mobile' : ''}`}>
      <div className="sidebar-navigation-wrapper">
        <div className="sidebar-main-title-wrapper">
            <span className="sidebar-main-title">{t("main", { ns: "mobile" })}</span>
      </div>

      <div className="sidebar-main-section">
        <nav className="sidebar-navlinks">
          {/* Dashboard - Always visible (no permission required) */}
          <button
            type="button"
            data-page-tour="dashboard"
            className={`sidebar-navlink${
              activeItem === "dashboard" ? " sidebar-navlink--active" : ""
            }`}
            onClick={() => {
              setActiveItem("dashboard");
              if (location.pathname === "/pages/dashboard" || location.pathname === "/dashboard") {
                window.dispatchEvent(new Event("dashboard:refresh"));
              }
              navigate("/pages/dashboard");
              closeSidebarOnMobile();
            }}
          >
            <span className="sidebar-navlink-icon">
              <img src={dashboardIcon} alt="Dashboard" className="sidebar-icon-img sidebar-icon-img--menu" />
            </span>
            <span className="sidebar-navlink-label">{t("dashboard", { ns: "mobile" })}</span>
          </button>

          {/* Accounts dropdown - show when user has account permissions (no KYC/profile lock at sidebar level) */}
          {showAccountsDropdown && (
            <div className={`sidebar-dropdown ${openDropdowns.accounts ? "open" : ""}`}>
              <button
                type="button"
                className="sidebar-navlink"
                onClick={() => toggleDropdown("accounts")}
              >
                <span className="sidebar-navlink-icon">
                  <img src={accountsIcon} alt="Accounts" className="sidebar-icon-img sidebar-icon-img--menu" />
                </span>
                <span className="sidebar-navlink-label">{t("accounts", { ns: "mobile" })}</span>
                <span className="sidebar-navlink-arrow">
                  <i className={`fa-solid fa-chevron-down ${openDropdowns.accounts ? "sidebar-arrow-open" : ""}`}></i>
                </span>
              </button>
              <div className="sidebar-dropdown-content">
                {/* List: always visible when Accounts dropdown is shown (permission checked on page) */}
                <div 
                  className={`sidebar-dropdown-item ${activeItem === "accounts-List" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="accounts_list"
                  onClick={() => {
                    handleDropdownItemClick("accounts", "List");
                    navigate("/pages/accounts/list");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("real_accounts", { ns: "mobile" })}</span>
                </div>
                <div
                  className={`sidebar-dropdown-item ${activeItem === "accounts-Demo Accounts" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="demo_accounts"
                  onClick={() => {
                    handleDropdownItemClick("accounts", "Demo Accounts");
                    navigate("/pages/accounts/demo");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("demo_accounts", { ns: "mobile" })}</span>
                </div>
              </div>
            </div>
          )}

          {/* Funds dropdown - completely hidden when account is approval locked
              (e.g., "Complete Your Profile" or "Account Approval Required") */}
          {(!approvalLocked && (canCashIn || showCashOutInSidebar || canInternalTransfer || canExchange || canDepositByCard || canDepositClient || canWithdrawClient)) && (
            <div className={`sidebar-dropdown ${openDropdowns.funds ? "open" : ""}`}>
              <button
                type="button"
                className="sidebar-navlink"
                onClick={() => toggleDropdown("funds")}
              >
                <span className="sidebar-navlink-icon">
                  <img src={fundsIcon} alt="Funds" className="sidebar-icon-img sidebar-icon-img--menu" />
                </span>
                <span className="sidebar-navlink-label">{t("funds", { ns: "mobile" })}</span>
                <span className="sidebar-navlink-arrow">
                  <i className={`fa-solid fa-chevron-down ${openDropdowns.funds ? "sidebar-arrow-open" : ""}`}></i>
                </span>
              </button>
              <div className="sidebar-dropdown-content">
                {canCashIn && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Cash-In" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="cash_in"
                    onClick={() => {
                      handleDropdownItemClick("funds", "Cash-In");
                      navigate("/pages/cash-in");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("cashin", { ns: "mobile" })}</span>
                  </div>
                )}
                {showCashOutInSidebar && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Cash-Out" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="cash_out"
                    onClick={() => {
                      handleDropdownItemClick("funds", "Cash-Out");
                      navigate("/pages/cash-out");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("cashout", { ns: "mobile" })}</span>
                  </div>
                )}
                {canInternalTransfer && (
                  <div
                    className={`sidebar-dropdown-item ${activeItem === "funds-Internal Transfer" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="transfer"
                    onClick={() => {
                      handleDropdownItemClick("funds", "Internal Transfer");
                      navigate("/pages/transfer");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("internal_transfer", { ns: "mobile" })}</span>
                  </div>
                )}
                {canDepositClient && !showIBDashboard && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Transfer To Client" ? "sidebar-dropdown-item--active" : ""}`}
                    onClick={() => handleDropdownItemClick("funds", "Transfer To Client")}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("transfer_to_client", { ns: "mobile" })}</span>
                  </div>
                )}
                {canWithdrawClient && !showIBDashboard && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Transfer From Client" ? "sidebar-dropdown-item--active" : ""}`}
                    onClick={() => handleDropdownItemClick("funds", "Transfer From Client")}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("transfer_from_client", { ns: "mobile" })}</span>
                  </div>
                )}
                {canExchange && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Exchange" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="exchange"
                    onClick={() => {
                      handleDropdownItemClick("funds", "Exchange");
                      navigate("/pages/exchange");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("exchange", { ns: "mobile" })}</span>
                  </div>
                )}
                {canDepositByCard && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "funds-Pay With Card" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="pay_with_card"
                    onClick={() => {
                      handleDropdownItemClick("funds", "Pay With Card");
                      navigate("/pages/pay-with-card");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("pay_with_card", { ns: "mobile" })}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transactions dropdown - also hidden while account is in locked status */}
          {(!approvalLocked && (canViewPendingRequest || canViewCashInRequests || canViewCashOutRequests || canViewHistory || canCollectTransaction || onTransactionsHistoryRoute || onCashInRequestsRoute || onCashOutRequestsRoute || onPendingTransactionsRoute)) && (
            <div className={`sidebar-dropdown ${openDropdowns.transactions ? "open" : ""}`}>
              <button
                type="button"
                className="sidebar-navlink"
                onClick={() => toggleDropdown("transactions")}
              >
                <span className="sidebar-navlink-icon">
                  <img src={transactionsIcon} alt="Transactions" className="sidebar-icon-img sidebar-icon-img--menu" />
                </span>
                <span className="sidebar-navlink-label">{t("transactions", { ns: "mobile" })}</span>
                <span className="sidebar-navlink-arrow">
                  <i className={`fa-solid fa-chevron-down ${openDropdowns.transactions ? "sidebar-arrow-open" : ""}`}></i>
                </span>
              </button>
              <div className="sidebar-dropdown-content">
                {canViewCashInRequests && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "transactions-Cash-In Requests" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="cash_in_requests"
                    onClick={() => {
                      handleDropdownItemClick("transactions", "Cash-In Requests");
                      navigate("/pages/transactions/cash-in-requests");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("cash_in_requests", { ns: "mobile" })}</span>
                  </div>
                )}
                  
                {canViewCashOutRequests && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "transactions-Cash-Out Requests" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="cash_out_requests"
                    onClick={() => {
                      handleDropdownItemClick("transactions", "Cash-Out Requests");
                      navigate("/pages/transactions/cash-out-requests");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("cash_out_requests", { ns: "mobile" })}</span>
                  </div>
                )}
                {canCollectTransaction && !showIBDashboard && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "transactions-Collect Transaction" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="collect_transaction"
                    onClick={() => {
                      handleDropdownItemClick("transactions", "Collect Transaction");
                      navigate("/pages/ibDashboard/collect-transaction");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("collect_transaction", { ns: "mobile" })}</span>
                  </div>
                )}
                {(canViewPendingRequest || onPendingTransactionsRoute) && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "transactions-Pending" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="pending_transactions"
                    onClick={() => {
                      handleDropdownItemClick("transactions", "Pending");
                      navigate("/pages/transactions/pending");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("pending_transaction", { ns: "mobile" })}</span>
                  </div>
                )}
                {(canViewHistory || onTransactionsHistoryRoute) && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "transactions-History" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="transactions_history"
                    onClick={() => {
                      handleDropdownItemClick("transactions", "History");
                      navigate("/pages/transactions/history");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("history", { ns: "mobile" })}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>
           <nav className="sidebar-navlinks">
          {showIBDashboard && (
            <div className={`sidebar-dropdown sidebar-dropdown--ib ${openDropdowns.ibDashboard ? "open" : ""}`}>
              <button
                type="button"
                data-page-tour="ib_dashboard"
                className={`sidebar-navlink${
                  activeItem === "ibDashboard-Overview" ? " sidebar-navlink--active" : ""
                }`}
                onClick={() => {
                  if (location.pathname === "/pages/ibDashboard/dashboard") {
                    window.dispatchEvent(new Event("ib-dashboard:refresh"));
                    toggleDropdown("ibDashboard");
                  } else {
                    navigate("/pages/ibDashboard/dashboard");
                    handleDropdownItemClick("ibDashboard", "Overview");
                    setOpenDropdowns((prev) => ({ ...prev, ibDashboard: true }));
                  }
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-navlink-icon">
                  <img src={ibDashboardIcon} alt="IB Dashboard" className="sidebar-icon-img sidebar-icon-img--menu" />
                </span>
                <span className="sidebar-navlink-label">{t("ib_dashboard", { ns: "mobile" })}</span>
                <span className="sidebar-navlink-arrow">
                  <i className={`fa-solid fa-chevron-down ${openDropdowns.ibDashboard ? "sidebar-arrow-open" : ""}`}></i>
                </span>
              </button>
              <div className="sidebar-dropdown-content">
                {canViewClientsList && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Clients List" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="clients_list"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Clients List");
                      // Navigate to IB clients list page
                      navigate("/pages/ibDashboard/clients-list");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("clients_list", { ns: "mobile" })}</span>
                  </div>
                )}
                {canViewClientsTree && (
                  <div
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Clients Tree" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="clients_tree"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Clients Tree");
                      navigate("/pages/ibDashboard/clients-tree");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("ib_clients_tree_tab", { ns: "mobile" }) || "Clients Tree"}</span>
                  </div>
                )}
                {canSendNotification && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Send Notification" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="send_notification"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Send Notification");
                      navigate("/pages/ibDashboard/send-notification");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("send_notification", { ns: "mobile" })}</span>
                  </div>
                )}
                {canDepositClient && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Transfer To Client" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="transfer_to_client"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Transfer To Client");
                      navigate("/pages/ibDashboard/transfer-to-client");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("transfer_to_client", { ns: "mobile" })}</span>
                  </div>
                )}
                {canWithdrawClient && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Transfer From Client" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="transfer_from_client"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Transfer From Client");
                      navigate("/pages/ibDashboard/transfer-from-client");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("transfer_from_client", { ns: "mobile" })}</span>
                  </div>
                )}
                {canCollectTransaction && (
                  <div 
                    className={`sidebar-dropdown-item ${activeItem === "ibDashboard-Collect Transaction" ? "sidebar-dropdown-item--active" : ""}`}
                    data-page-tour="collect_transaction"
                    onClick={() => {
                      handleDropdownItemClick("ibDashboard", "Collect Transaction");
                      navigate("/pages/ibDashboard/collect-transaction");
                      closeSidebarOnMobile();
                    }}
                  >
                    <span className="sidebar-dropdown-circle"></span>
                    <span className="sidebar-dropdown-title">{t("collect_transaction", { ns: "mobile" })}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Marketing Tools section - always visible */}
          <div className={`sidebar-dropdown ${openDropdowns.marketingTools ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-navlink"
              onClick={() => toggleDropdown("marketingTools")}
            >
              <span className="sidebar-navlink-icon">
                <i className="fa-solid fa-bullhorn" aria-hidden />
              </span>
              <span className="sidebar-navlink-label">
                {t("marketing_tools", { ns: "mobile", defaultValue: "Marketing Tools" })}
              </span>
              <span className="sidebar-navlink-arrow">
                <i className={`fa-solid fa-chevron-down ${openDropdowns.marketingTools ? "sidebar-arrow-open" : ""}`}></i>
              </span>
            </button>
            <div className="sidebar-dropdown-content">
              <div
                className={`sidebar-dropdown-item ${
                  activeItem === "marketingTools-Marketing Plan" ? "sidebar-dropdown-item--active" : ""
                }`}
                data-page-tour="marketing_tools"
                onClick={() => {
                  handleDropdownItemClick("marketingTools", "Marketing Plan");
                  navigate("/pages/marketing-tools");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">
                  {t("marketing_plan", { ns: "mobile", defaultValue: "Marketing Plan" })}
                </span>
              </div>
              {canViewBrokerTools && (
                <div
                  className={`sidebar-dropdown-item ${
                    activeItem === "marketingTools-Broker Tools" ? "sidebar-dropdown-item--active" : ""
                  }`}
                  data-page-tour="broker_tools"
                  onClick={() => {
                    handleDropdownItemClick("marketingTools", "Broker Tools");
                    navigate("/pages/ibDashboard/broker-tools");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">
                    {t("ib_broker_tools", { ns: "mobile", defaultValue: "Broker Tools" })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Profile - Always visible (no permission required) */}
          <div className={`sidebar-dropdown sidebar-dropdown--profile ${openDropdowns.profile ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-navlink"
              onClick={() => toggleDropdown("profile")}
            >
              <span className="sidebar-navlink-icon">
                <img src={userProfileIcon} alt="Profile" className="sidebar-icon-img sidebar-icon-img--menu" />
              </span>
              <span className="sidebar-navlink-label">{t("profile", { ns: "mobile" })}</span>
              <span className="sidebar-navlink-arrow">
                <i className={`fa-solid fa-chevron-down ${openDropdowns.profile ? "sidebar-arrow-open" : ""}`}></i>
              </span>
            </button>
            <div className="sidebar-dropdown-content">
                {/* Profile sub-items - no permissions required */}
                <div 
                  className={`sidebar-dropdown-item ${activeItem === "profile-Profile" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="profile_information"
                  onClick={() => {
                    handleDropdownItemClick("profile", "Profile");
                    navigate("/pages/profile-information");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("profile_information", { ns: "mobile" })}</span>
                </div>
                <div
                  className={`sidebar-dropdown-item ${activeItem === "profile-Regulation" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="user_regulation"
                  onClick={() => {
                    handleDropdownItemClick("profile", "Regulation");
                    navigate("/pages/user-regulation");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("user_regulation", { ns: "mobile" })}</span>
                </div>
                <div
                  className={`sidebar-dropdown-item ${activeItem === "profile-Trading Info" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="trading_information"
                  onClick={() => {
                    handleDropdownItemClick("profile", "Trading Info");
                    navigate("/pages/trading-information");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("trading_information", { ns: "mobile" })}</span>
                </div>
                <div
                  className={`sidebar-dropdown-item ${activeItem === "profile-Financial Info" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="financial_information"
                  onClick={() => {
                    handleDropdownItemClick("profile", "Financial Info");
                    navigate("/pages/financial-information");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("financial_information", { ns: "mobile" })}</span>
                </div>
            </div>
          </div>
        </nav>
      </div>

      <div className="sidebar-other-section">
        <div className="sidebar-other-title-wrapper">
          <span className="sidebar-other-title">{t("other", { ns: "mobile" })}</span>
        </div>

        <nav className="sidebar-navlinks">
          {/* Settings - Always visible (no permission required, but chat message requires PER__SEND_MESSAGE) */}
          <div className={`sidebar-dropdown ${openDropdowns.settings ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-navlink"
              onClick={() => toggleDropdown("settings")}
            >
              <span className="sidebar-navlink-icon">
                <img src={settingsIcon} alt="Settings" className="sidebar-icon-img" />
              </span>
              <span className="sidebar-navlink-label">{t("settings", { ns: "mobile" })}</span>
              <span className="sidebar-navlink-arrow">
                <i className={`fa-solid fa-chevron-down ${openDropdowns.settings ? "sidebar-arrow-open" : ""}`}></i>
              </span>
            </button>
            <div className="sidebar-dropdown-content">
                {/* Settings sub-items - no permissions required (chat message would require PER__SEND_MESSAGE if added) */}
                <div 
                  className={`sidebar-dropdown-item ${activeItem === "settings-Change Password" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="change_pin"
                  onClick={() => {
                    handleDropdownItemClick("settings", "Change Password");
                    navigate("/pages/settings/change-pin");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("change_password", { ns: "mobile" })}</span>
                </div>
                <div 
                  className={`sidebar-dropdown-item ${activeItem === "settings-Active Devices" ? "sidebar-dropdown-item--active" : ""}`}
                  data-page-tour="active_devices"
                  onClick={() => {
                    handleDropdownItemClick("settings", "Active Devices");
                    navigate("/pages/settings/active-devices");
                    closeSidebarOnMobile();
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">{t("active_devices", { ns: "mobile" })}</span>
                </div>
                <div
                  className={`sidebar-dropdown-item ${activeItem === "settings-Translated" ? "sidebar-dropdown-item--active" : ""}`}
                  onClick={() => {
                    handleDropdownItemClick("settings", "Translated");
                    setShowTranslationDialog(true);
                  }}
                >
                  <span className="sidebar-dropdown-circle"></span>
                  <span className="sidebar-dropdown-title">
                    {t("language", { ns: "mobile" }) || "Translated"}
                  </span>
                </div>
            </div>
          </div>

          <div className={`sidebar-dropdown ${openDropdowns.legal ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-navlink"
              onClick={() => toggleDropdown("legal")}
            >
              <span className="sidebar-navlink-icon sidebar-navlink-icon--no-border">
                <i className="fa-solid fa-scale-balanced"></i>
              </span>
              <span className="sidebar-navlink-label">{t("legal", { ns: "mobile" })}</span>
              <span className="sidebar-navlink-arrow">
                <i className={`fa-solid fa-chevron-down ${openDropdowns.legal ? "sidebar-arrow-open" : ""}`}></i>
              </span>
            </button>
            <div className="sidebar-dropdown-content">
              <div
                className={`sidebar-dropdown-item ${activeItem === "legal-Privacy Policy" ? "sidebar-dropdown-item--active" : ""}`}
                data-page-tour="privacy_policy"
                onClick={() => {
                  handleDropdownItemClick("legal", "Privacy Policy");
                  navigate("/pages/legal/privacy-policy");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">{t("privacy_policy", { ns: "mobile" })}</span>
              </div>
              <div
                className={`sidebar-dropdown-item ${activeItem === "legal-Terms of Services" ? "sidebar-dropdown-item--active" : ""}`}
                data-page-tour="terms_of_services"
                onClick={() => {
                  handleDropdownItemClick("legal", "Terms of Services");
                  navigate("/pages/legal/terms-of-services");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">{t("terms_of_services", { ns: "mobile" })}</span>
              </div>
              <div
                className={`sidebar-dropdown-item ${activeItem === "legal-Risk Disclosure" ? "sidebar-dropdown-item--active" : ""}`}
                data-page-tour="risk_disclosure"
                onClick={() => {
                  handleDropdownItemClick("legal", "Risk Disclosure");
                  navigate("/pages/legal/risk-disclosure");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">{t("risk_disclosure", { ns: "mobile" })}</span>
              </div>
            </div>
          </div>

          <div className={`sidebar-dropdown ${openDropdowns.support ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-navlink"
              onClick={() => toggleDropdown("support")}
            >
              <span className="sidebar-navlink-icon sidebar-navlink-icon--no-border">
                <i className="fa-solid fa-headset"></i>
              </span>
              <span className="sidebar-navlink-label">{t("support", { ns: "mobile" })}</span>
              <span className="sidebar-navlink-arrow">
                <i className={`fa-solid fa-chevron-down ${openDropdowns.support ? "sidebar-arrow-open" : ""}`}></i>
              </span>
            </button>
            <div className="sidebar-dropdown-content">
              <div
                className={`sidebar-dropdown-item ${activeItem === "support-FAQ" ? "sidebar-dropdown-item--active" : ""}`}
                data-page-tour="faq"
                onClick={() => {
                  handleDropdownItemClick("support", "FAQ");
                  navigate("/pages/support/faq");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">{t("faq", { ns: "mobile" })}</span>
              </div>
              <div
                className={`sidebar-dropdown-item ${activeItem === "support-About Us" ? "sidebar-dropdown-item--active" : ""}`}
                data-page-tour="about_us"
                onClick={() => {
                  handleDropdownItemClick("support", "About Us");
                  navigate("/pages/support/about-us");
                  closeSidebarOnMobile();
                }}
              >
                <span className="sidebar-dropdown-circle"></span>
                <span className="sidebar-dropdown-title">{t("about_us", { ns: "mobile" })}</span>
              </div>
            </div>
          </div>

          {/* Theme Toggle - Always visible */}
          <button
            type="button"
            className="sidebar-navlink sidebar-theme-toggle"
            onClick={toggleTheme}
            aria-label={isDarkMode ? t("switch_to_light_mode", { ns: "mobile" }) : t("switch_to_dark_mode", { ns: "mobile" })}
          >
            <span className="sidebar-navlink-icon sidebar-navlink-icon--no-border">
              <i className={isDarkMode ? "fa-solid fa-sun" : "fa-solid fa-moon"}></i>
            </span>
            <span className="sidebar-navlink-label">{isDarkMode ? t("light_mode", { ns: "mobile" }) : t("dark_mode", { ns: "mobile" })}</span>
          </button>

          {/* Logout - Always visible */}
          <button
            type="button"
            className="sidebar-navlink"
            onClick={handleLogout}
            aria-label={t("logout", { ns: "mobile" })}
          >
            <span className="sidebar-navlink-icon sidebar-navlink-icon--no-border">
              <i className="fa-solid fa-sign-out-alt"></i>
            </span>
            <span className="sidebar-navlink-label">{t("logout", { ns: "mobile" })}</span>
          </button>
        </nav>
      </div>
      </div>

      <div className="sidebar-spacer"></div>

      <div className="sidebar-footer">
        <div className="sidebar-user-menu-wrap">
          <button
            type="button"
            className={`sidebar-profile-picture${
              !profileUrl || sidebarAvatarLoadFailed ? " sidebar-profile-picture--initial" : ""
            }`}
            onClick={() => {
              navigate("/pages/profile-information");
              closeSidebarOnMobile();
            }}
            aria-label={t("profile", { ns: "mobile" })}
          >
            {profileUrl && !sidebarAvatarLoadFailed ? (
              <img
                src={profileUrl}
                alt=""
                className="sidebar-profile-image"
                onError={() => setSidebarAvatarLoadFailed(true)}
              />
            ) : (
              <span className="sidebar-profile-initial" aria-hidden>
                {sidebarUserDisplayInitial}
              </span>
            )}
          </button>
        </div>
        <div className="sidebar-user-info">
          <span className="sidebar-user-name">
            <span className="sidebar-user-name-text">
              {ProfileInfoController.getDisplayName(sidebarUser || {})}
            </span>
            {sidebarCustomerId ? (
              <span className="sidebar-user-id">#{sidebarCustomerId}</span>
            ) : null}
          </span>
          <span className="sidebar-user-email">
            {sidebarUser?.user_information?.EMAIL ||
              sidebarUser?.userInformation?.EMAIL ||
              sessionStorage.getItem("userEmail") ||
              sessionStorage.getItem("email") ||
              localStorage.getItem("userEmail") ||
              localStorage.getItem("email") ||
              ""}
          </span>
        </div>
      </div>
    </aside>
    {showTranslationDialog && (
      <div
        className="sidebar-translation-overlay"
        onClick={() => setShowTranslationDialog(false)}
      >
        <div
          className="sidebar-translation-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sidebar-translation-modal-title">
            {t("language", { ns: "mobile" }) || "Translated"}
          </div>
          <div className="sidebar-translation-dialog">
            {[
              { code: "en", key: "en" },
              { code: "ar", key: "ar" },
              { code: "fr", key: "fr" },
            ].map((lang) => {
              const isActive = (i18n.language || "en").split("-")[0] === lang.code;
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`sidebar-translation-option ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setShowTranslationDialog(false);
                    closeSidebarOnMobile();
                    changeLanguageWithReload(lang.code, "/pages/dashboard");
                  }}
                >
                  {t(lang.key, { ns: "mobile" }) || lang.code.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    )}
    <Popup
      isOpen={showLogoutDialog}
      onClose={() => setShowLogoutDialog(false)}
      onCancel={() => setShowLogoutDialog(false)}
      onConfirm={handleConfirmLogout}
      showCancel={true}
      title={t("logout", { ns: "mobile" })}
      message={t("logout_confirmation", {
        ns: "mobile",
        defaultValue: "Are you sure you want to log out?",
      })}
      instruction=""
      buttonText={t("yes", { ns: "mobile", defaultValue: "Yes" })}
      cancelButtonText={t("no", { ns: "mobile", defaultValue: "No" })}
      icon="fa-sign-out-alt"
    />
    {!hideChatOnTablePages && (
      <SupportChatWidget hideLauncher={hideSupportChatLauncher} />
    )}
    </>
  );
}

